import { useId } from "react";
import { cn } from "@/lib/cn";
import { FieldFrame, fieldStateClasses, fieldSurface } from "./field";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  leftIcon?: React.ReactNode;
  containerClassName?: string;
}

export function Input({
  label,
  hint,
  error,
  required,
  leftIcon,
  id,
  className,
  containerClassName,
  "aria-describedby": ariaDescribedBy,
  ...props
}: InputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasError = Boolean(error);
  const errorId = `${inputId}-error`;
  const describedBy =
    [ariaDescribedBy, hasError ? errorId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      errorId={errorId}
      required={required}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-white/40">
            {leftIcon}
          </span>
        )}
        <input
          id={inputId}
          required={required}
          aria-invalid={hasError || undefined}
          aria-describedby={describedBy}
          className={cn(
            fieldSurface,
            fieldStateClasses(hasError),
            "h-11",
            leftIcon && "pl-11",
            className,
          )}
          {...props}
        />
      </div>
    </FieldFrame>
  );
}
